import { AuditCriteriaValueCell } from 'models/auditCriteriaGeneric';
import { AuditCriterionDTO } from './auditCriteriaDTO';

// PLAN-075: the DTOs of generic criteria sets, assessment tasks, the criteria values, the "Answered by" automations
// and the AI outcomes, mirrored from the server's Core/Data/DTOs/AuditCriteria. Enum fields are numbers (the enums
// are in models/auditCriteriaGeneric.ts).

export class GenericAuditCriteriaSetRowDTO {
  auditCriteriaSetId: number = 0;

  entityType: number = 0;

  subType: number | null = null;

  activeCount: number = 0;

  modifiedOn?: string = undefined;

  reviewedOn?: string = undefined;

  headVersion: number | null = null;

  affectedEntityCount: number = 0;

  copyCount: number = 0;
}

export class GenericAuditCriteriaSetCreateDTO {
  entityType: number = 0;

  subType: number | null = null;

  copyFromFamilySet: boolean = false;
}

export class AuditCriteriaResolutionDTO {
  entityType: number = 0;

  entityId: number = 0;

  layer: number = 0;

  auditCriteriaSetId: number | null = null;

  setEntityId: number | null = null;

  taskTypeFamilyId: number | null = null;

  headTaskTypeId: number | null = null;

  headVersion: number | null = null;
}

export class AuditCriteriaCompareDTO {
  auditCriteriaSetId: number = 0;

  sourceSetId: number | null = null;

  copiedOn?: string = undefined;

  sourceChangedOn?: string = undefined;

  rows: AuditCriteriaCompareRowDTO[] = [];
}

export class AuditCriteriaCompareRowDTO {
  lineageKey: string = '';

  // Same, Changed, OnlyInCopy or OnlyInSource
  changeKind: string = '';

  copy: AuditCriterionDTO | null = null;

  source: AuditCriterionDTO | null = null;
}

export class AssessmentTaskCreateDTO {
  entityType: number = 0;

  entityIds: number[] = [];

  // the task starts here; without a deadline it is due 14 days later
  startDate: string | null = null;

  deadline?: string = undefined;

  assigneeId?: string = undefined;

  // the AI evidence audit of each item (PLAN-077) starts in the background with the new task as its consumer
  runEvidenceAudit: boolean = false;
}

export class AssessmentTaskResultDTO {
  created: AssessmentTaskCreatedDTO[] = [];

  refused: AssessmentTaskRefusalDTO[] = [];
}

export class AssessmentTaskCreatedDTO {
  entityType: number = 0;

  entityId: number = 0;

  taskId: number = 0;

  // the AI evidence audit the server started in the background host, or why not; null when it was not chosen
  evidenceAudit: AssessmentEvidenceAuditStartDTO | null = null;
}

export class AssessmentEvidenceAuditStartDTO {
  started: boolean = false;

  // 'NoCredits' | 'NotAuthorized' | 'NoSet' | 'NoCriteria' | 'Failed'
  reason: string | null = null;
}

export class AssessmentTaskRefusalDTO {
  entityType: number = 0;

  entityId: number = 0;

  code: string = '';

  message: string = '';
}

// The start card (PLAN-CTI-090): the AI evidence audit entry and the assignee pre-fill
export class AssessmentAutomationsDTO {
  // the assignee of the item's latest assessment task; null without one, when it cannot be read, and for a batch
  latestAssigneeId: string | null = null;

  // the AI evidence audit entry (PLAN-077): the criteria it answers; null when it is not offered
  evidenceAudit: AssessmentEvidenceAuditDTO | null = null;
}

export class AssessmentEvidenceAuditDTO {
  criteriaCount: number = 0;

  offered: boolean = false;
}

export class AuditCriteriaValuesQueryDTO {
  entityType: number = 0;

  // null: every entity of the family the user may read
  entityIds: number[] | null = null;

  subType: number | null = null;

  period: number = 0;

  from?: string = undefined;

  to?: string = undefined;

  lineageKeys: string[] | null = null;
}

export class AuditCriteriaValuesResultDTO {
  columns: AuditCriteriaValueColumnDTO[] = [];

  // values[entityId][lineageKey]: the compact cell (models/auditCriteriaGeneric.ts); an entity the user may not read and
  // a column without an answer in the period are absent
  values: Record<string, Record<string, AuditCriteriaValueCell>> = {};

  // outcomes[entityId][lineageKey]: the latest standing AI outcome (Proposed or Applied), not bound to the period
  outcomes: Record<string, Record<string, AuditCriteriaValueOutcomeDTO>> = {};

  from?: string = undefined;

  to?: string = undefined;
}

// the result of POST tenants/criteria/values/rebuild (tenant global admin)
export class AuditCriterionValueRebuildResultDTO {
  tenants: number = 0;

  rows: number = 0;

  failed: AuditCriterionValueRebuildFailureDTO[] = [];
}

export class AuditCriterionValueRebuildFailureDTO {
  tenantId: string = '';

  message: string = '';
}

export class AuditCriteriaValueOutcomeDTO {
  outcomeId: number = 0;

  proposedResult: number = 0;

  // AICriterionOutcomeSourceKind, and the ref ("<process>:<run>" for a process run)
  sourceKind: number = 0;

  sourceRef: string = '';

  // empty when the automation no longer exists
  sourceName: string = '';

  created: string = '';
}

export class AuditCriteriaValueColumnDTO {
  lineageKey: string = '';

  requirement: string = '';

  // the criterion's short header; null without one
  columnName: string | null = null;

  mode: string = '';

  auditCriteriaSetId: number = 0;

  subType: number | null = null;

  specific: boolean = false;

  sortOrder: number = 0;
}

export class AICriterionOutcomeDTO {
  aiCriterionOutcomeId: number = 0;

  entityType: number = 0;

  entityId: number = 0;

  lineageKey: string = '';

  auditCriteriaSetId: number | null = null;

  sourceKind: number = 0;

  sourceRef: string = '';

  sourceName: string = '';

  proposedResult: number = 0;

  summary: string = '';

  evidenceLinkIds: number[] = [];

  status: number = 0;

  appliedToTaskId: number | null = null;

  created: string = '';

  appliedOn?: string = undefined;
}
